/**
 * Hotspot pressure (pillar B, design D8) — pure aggregation.
 *
 *   weight(c)   = priority_score(c)   (pillar A's stored score; it already
 *                 counts reach and recurrence, so nothing is multiplied
 *                 on top — no double counting)
 *   exposure(h) = steps walked in h during the window
 *   pressure(h) = 10 × (Σ weight(c) + k × μ) / (exposure(h) + k)
 *
 * The sum runs over problem canonicals (hinders / missing / incorrect)
 * observed in h during the window whose state still counts against
 * quality: the open states plus `fix_applied` at full weight — quality
 * only moves on re-run evidence. `k` = 10 steps, `μ` = the project's
 * mean weight per step over the same window (Bayesian smoothing, so a
 * one-step area cannot top the list). `helps` never add pressure; they
 * are counted as strengths. The human-test outcome is a separate column.
 *
 * An area walked with no unresolved problem is `clear` with pressure 0:
 * the prior only tempers areas that have problems. Showing the bare
 * prior there read as pressure next to "0 open", and put problem-free
 * areas under "Weakest areas".
 *
 * The database layer loads a `HotspotData` snapshot; everything that
 * decides a number lives here so it is unit-tested.
 */
import { formatGroupingKey } from "../grouping-keys.js";
import type { HotspotBy } from "./hotspot-query.js";

/** Smoothing prior strength in steps. */
export const HOTSPOT_PRIOR_K = 10;

/**
 * Canonical states that add pressure — `UNRESOLVED_STATES` in
 * `@iamu/db` (open states + `fix_applied`); a db test keeps them equal.
 */
export const HOTSPOT_PRESSURE_STATES = [
  "new",
  "open",
  "in_progress",
  "regressed",
  "fix_applied",
] as const;

const OPEN_STATES = new Set(["new", "open", "in_progress", "regressed"]);
const PRESSURE_STATES = new Set<string>(HOTSPOT_PRESSURE_STATES);
const PROBLEM_TYPES = new Set(["hinders", "missing", "incorrect"]);

/** Human-test rows for findings no judge verdict cites. */
export const HOTSPOT_UNLINKED_KEY = "unlinked";

// ── Input snapshot ──────────────────────────────────────────────────

export interface HotspotRunData {
  id: string;
  scenarioId: string;
  personaId: string;
  outlineId: string;
  /** ISO timestamp; runs are ordered by start, never completion. */
  startedAt: string;
  steps: number;
  /** Steps per route key; steps without a page URL are not listed. */
  routeSteps: Record<string, number>;
  primaryCapabilityId: string | null;
  secondaryCapabilityIds: string[];
  /** Judge ratings of this run (yes | partially | no | not_applicable). */
  verdicts: Array<{ humanTestId: string; rating: string }>;
}

/** A member finding of a canonical, observed in a window run. */
export interface HotspotObservation {
  canonicalId: string;
  findingId: string;
  walkthroughId: string;
  routeKey: string | null;
}

export interface HotspotCanonicalData {
  id: string;
  state: string;
  stateReason?: string | null;
  type: string | null;
  severity?: string | null;
  priorityScore: number | null;
  priorityOverride?: string | null;
  observationCount: number;
  title: string | null;
  firstSeenWalkthroughId?: string | null;
}

export interface HotspotResolvedFinding {
  canonicalId: string;
  walkthroughId: string;
  scenarioId?: string | null;
}

/** A judge verdict citing a finding (`verdict_evidence`). */
export interface HotspotCitation {
  walkthroughId: string;
  humanTestId: string;
  findingId: string;
}

export interface HotspotEntities {
  capabilities: Array<{ id: string; name: string }>;
  personas: Array<{ id: string; name: string }>;
  scenarios: Array<{ id: string; label: string }>;
  humanTests: Array<{
    outlineId: string;
    humanTestId: string;
    question: string;
    outlineName: string;
  }>;
}

/** Human-test labels from outlines (`outlines.human_tests`). */
export function humanTestEntities(
  outlines: Array<{
    id: string;
    name: string;
    humanTests: Array<{ id: string; question: string }> | null;
  }>,
): HotspotEntities["humanTests"] {
  return outlines.flatMap((o) =>
    (o.humanTests ?? []).map((t) => ({
      outlineId: o.id,
      humanTestId: t.id,
      question: t.question,
      outlineName: o.name,
    })),
  );
}

export interface HotspotData {
  runs: HotspotRunData[];
  observations: HotspotObservation[];
  canonicals: HotspotCanonicalData[];
  citations: HotspotCitation[];
  entities: HotspotEntities;
  resolved?: HotspotResolvedFinding[];
}

export interface HotspotOptions {
  includeSecondary?: boolean;
  k?: number;
  /** Use this prior mean instead of the snapshot's (matrix cells). */
  mu?: number;
}

// ── Output ──────────────────────────────────────────────────────────

export type HotspotDirection = "better" | "worse" | "flat";
/**
 * measured (walked, has unresolved problems) · clear (walked, none —
 * pressure 0) · not_walked (no steps).
 */
export type HotspotStatus = "measured" | "clear" | "not_walked";

export interface HotspotTrendPoint {
  walkthroughId: string;
  startedAt: string;
  scenarioId: string;
  steps: number;
  weight: number;
  /** Unsmoothed weight per 10 steps in this run. */
  value: number;
  /** Share of this run's rated human tests in the area answered "yes". */
  passRate: number | null;
}

export interface HotspotOutcome {
  pass: number;
  partial: number;
  fail: number;
}

export interface HotspotFindingDeltas {
  /** Newly discovered problem canonicals first observed in this window. */
  new: number;
  /** Problem canonicals verified/resolved in this window. */
  fixed: number;
  /** Canonicals that are currently in 'regressed' state. */
  regressed: number;
  /** Canonicals marked fix_applied that failed re-verification. */
  fixDidntHold: number;
  /** Net problem trajectory: (new + regressed) - fixed. Lower is better. */
  net: number;
}

export interface HotspotVerdictDeltas {
  /** Human tests that improved rating across window runs. */
  improved: number;
  /** Human tests whose rating remained unchanged. */
  unchanged: number;
  /** Human tests that declined in rating across window runs. */
  declined: number;
  /** Human tests that specifically declined to 'no'. */
  declinedToNo: number;
}

export interface HotspotRow {
  key: string;
  label: string;
  /** Context for the label (the scenario of a human test). */
  context: string | null;
  status: HotspotStatus;
  /** Smoothed pressure; 0 when clear, null when the area was not walked. */
  pressure: number | null;
  weightSum: number;
  exposureSteps: number;
  runs: number;
  /** Problem canonicals in an open state (new, open, in progress, regressed). */
  openProblems: number;
  /** `fix_applied` — counted at full weight until a re-run verifies. */
  awaitingRerun: number;
  regressed: number;
  /** Open problems (a subset of `openProblems`) seen in more than one run. */
  recurring: number;
  strengths: number;
  /** Canonicals from runs where this capability is a secondary tag. */
  alsoTagged: number | null;
  personas: number;
  outcome: HotspotOutcome | null;
  trend: HotspotTrendPoint[];
  direction: HotspotDirection | null;
  /** Finding-level deltas in this window. */
  deltas: HotspotFindingDeltas;
  /** Human test verdict deltas across window runs. */
  verdictDeltas: HotspotVerdictDeltas;
}

export interface HotspotCoverage {
  keyed: number;
  total: number;
  unit: "steps" | "findings";
}

export interface HotspotPrior {
  k: number;
  /** Project mean weight per step over the window. */
  mu: number;
}

export interface HotspotList {
  by: HotspotBy;
  rows: HotspotRow[];
  coverage: Record<HotspotBy, HotspotCoverage>;
  prior: HotspotPrior;
  totals: {
    runs: number;
    steps: number;
    /** Distinct canonicals adding pressure (rows can share one). */
    problems: number;
    weightSum: number;
  };
}

export type HotspotMember = HotspotCanonicalData;

/** Display extras of a member (loaded for the drill-down only). */
export interface HotspotMemberView {
  preview: string | null;
  personaName: string | null;
  /** The step to show as evidence: latest window observation with one. */
  evidence: {
    walkthroughId: string;
    stepNumber: number;
    targetBox?: {
      x: number;
      y: number;
      width: number;
      height: number;
      viewportWidth?: number;
      viewportHeight?: number;
    } | null;
  } | null;
}

export interface HotspotFailingTest {
  outlineId: string;
  humanTestId: string;
  question: string;
  scenarioName: string;
  ratings: Array<{ walkthroughId: string; startedAt: string; rating: string }>;
  citedCanonicalIds: string[];
}

export interface HotspotDetail {
  row: HotspotRow;
  prior: HotspotPrior;
  failingTests: HotspotFailingTest[];
  /** Problems adding pressure, by score; then everything else. */
  members: { pressure: HotspotMember[]; other: HotspotMember[] };
  strengths: HotspotMember[];
  /** Where this area's problems also show up, per other key. */
  distribution: Partial<
    Record<HotspotBy, Array<{ key: string; label: string; count: number }>>
  >;
}

// ── Pure formulas ───────────────────────────────────────────────────

/** Project mean weight per step. */
export function pressurePrior(totalWeight: number, totalSteps: number) {
  return totalSteps > 0 ? totalWeight / totalSteps : 0;
}

/**
 * `10 × (weightSum + k × mu) / (exposureSteps + k)`; null when the area
 * has no steps in the window (never shown as healthy).
 */
export function computePressure(input: {
  weightSum: number;
  exposureSteps: number;
  mu: number;
  k?: number;
}): number | null {
  const k = input.k ?? HOTSPOT_PRIOR_K;
  if (input.exposureSteps <= 0) return null;
  return (10 * (input.weightSum + k * input.mu)) / (input.exposureSteps + k);
}

/** Unsmoothed weight per 10 steps (one run's trend point). */
export function rawPressure(weight: number, steps: number): number {
  return steps > 0 ? (10 * weight) / steps : 0;
}

/**
 * Direction of the latest run against the earlier runs in the window
 * (their mean; with two runs, the previous one): lower is better.
 * Changes under 10% of the earlier level (and under 0.25 absolute) read
 * as flat. Null with fewer than two points.
 */
export function trendDirection(
  values: number[],
  deltas?: HotspotFindingDeltas,
  verdictDeltas?: HotspotVerdictDeltas,
): HotspotDirection | null {
  if (values.length < 2) return null;
  if (deltas) {
    if (
      deltas.regressed > 0 ||
      (verdictDeltas && verdictDeltas.declinedToNo > 0)
    ) {
      return "worse";
    }
    if (deltas.net < 0 && (!verdictDeltas || verdictDeltas.declined === 0)) {
      return "better";
    }
  }
  const earlier = values.slice(0, -1);
  const prev = earlier.reduce((s, v) => s + v, 0) / earlier.length;
  const last = values[values.length - 1];
  const delta = last - prev;
  if (Math.abs(delta) < Math.max(0.25, Math.abs(prev) * 0.1)) return "flat";
  return delta < 0 ? "better" : "worse";
}

const round = (n: number, d = 2) => {
  const f = 10 ** d;
  return Math.round(n * f) / f;
};

// ── Membership ──────────────────────────────────────────────────────

const key = {
  capability: (id: string) =>
    formatGroupingKey({ kind: "capability", capabilityId: id }),
  scenario: (id: string) =>
    formatGroupingKey({ kind: "scenario", scenarioId: id }),
  persona: (id: string) =>
    formatGroupingKey({ kind: "persona", personaId: id }),
  humanTest: (outlineId: string, humanTestId: string) =>
    formatGroupingKey({ kind: "human_test", outlineId, humanTestId }),
  route: (routeKey: string) => formatGroupingKey({ kind: "route", routeKey }),
};

const RUN_LEVEL = new Set<HotspotBy>(["capability", "scenario", "persona"]);

/** Keys a run walks, with the steps it walked in each. */
function runExposure(
  run: HotspotRunData,
  by: HotspotBy,
  includeSecondary: boolean,
): Array<[string, number]> {
  switch (by) {
    case "capability": {
      const ids = [
        ...(run.primaryCapabilityId ? [run.primaryCapabilityId] : []),
        ...(includeSecondary ? run.secondaryCapabilityIds : []),
      ];
      return [...new Set(ids)].map((id) => [key.capability(id), run.steps]);
    }
    case "scenario":
      return [[key.scenario(run.scenarioId), run.steps]];
    case "persona":
      return [[key.persona(run.personaId), run.steps]];
    case "human_test": {
      const tests = new Set(run.verdicts.map((v) => v.humanTestId));
      return [
        ...[...tests].map(
          (t) =>
            [key.humanTest(run.outlineId, t), run.steps] as [string, number],
        ),
        [HOTSPOT_UNLINKED_KEY, run.steps],
      ];
    }
    case "route":
      return Object.entries(run.routeSteps).map(([r, n]) => [key.route(r), n]);
  }
}

interface Index {
  runs: Map<string, HotspotRunData>;
  canonicals: Map<string, HotspotCanonicalData>;
  /** `${walkthroughId}|${findingId}` → cited human test ids. */
  cited: Map<string, string[]>;
}

function buildIndex(data: HotspotData): Index {
  const cited = new Map<string, string[]>();
  for (const c of data.citations) {
    const k = `${c.walkthroughId}|${c.findingId}`;
    const list = cited.get(k) ?? [];
    if (!list.includes(c.humanTestId)) list.push(c.humanTestId);
    cited.set(k, list);
  }
  return {
    runs: new Map(data.runs.map((r) => [r.id, r])),
    canonicals: new Map(data.canonicals.map((c) => [c.id, c])),
    cited,
  };
}

/** Keys an observation belongs to. */
function observationKeys(
  obs: HotspotObservation,
  run: HotspotRunData,
  by: HotspotBy,
  idx: Index,
  includeSecondary: boolean,
): string[] {
  if (RUN_LEVEL.has(by)) {
    return runExposure(run, by, includeSecondary).map(([k]) => k);
  }
  if (by === "route") return obs.routeKey ? [key.route(obs.routeKey)] : [];
  const tests = idx.cited.get(`${obs.walkthroughId}|${obs.findingId}`);
  return tests?.length
    ? tests.map((t) => key.humanTest(run.outlineId, t))
    : [HOTSPOT_UNLINKED_KEY];
}

interface Groups {
  exposure: Map<string, number>;
  runs: Map<string, Set<string>>;
  canonicals: Map<string, Set<string>>;
  /** key → run → canonicals observed in that run in the area. */
  perRun: Map<string, Map<string, Set<string>>>;
}

function addTo<K, V>(m: Map<K, Set<V>>, k: K, v: V) {
  const s = m.get(k) ?? new Set<V>();
  s.add(v);
  m.set(k, s);
}

function groupData(
  data: HotspotData,
  idx: Index,
  by: HotspotBy,
  includeSecondary: boolean,
): Groups {
  const g: Groups = {
    exposure: new Map(),
    runs: new Map(),
    canonicals: new Map(),
    perRun: new Map(),
  };
  for (const run of data.runs) {
    for (const [k, steps] of runExposure(run, by, includeSecondary)) {
      g.exposure.set(k, (g.exposure.get(k) ?? 0) + steps);
      addTo(g.runs, k, run.id);
    }
  }
  for (const obs of data.observations) {
    const run = idx.runs.get(obs.walkthroughId);
    if (!run || !idx.canonicals.has(obs.canonicalId)) continue;
    for (const k of observationKeys(obs, run, by, idx, includeSecondary)) {
      addTo(g.canonicals, k, obs.canonicalId);
      const byRun = g.perRun.get(k) ?? new Map<string, Set<string>>();
      addTo(byRun, run.id, obs.canonicalId);
      g.perRun.set(k, byRun);
    }
  }
  return g;
}

const isProblem = (c: HotspotCanonicalData) =>
  c.type != null && PROBLEM_TYPES.has(c.type);
/** Adds pressure: an unresolved, scored problem. */
const addsPressure = (c: HotspotCanonicalData) =>
  isProblem(c) && PRESSURE_STATES.has(c.state) && c.priorityScore != null;
const weightOf = (c: HotspotCanonicalData) =>
  addsPressure(c) ? (c.priorityScore ?? 0) : 0;
/** Counts in a historical trend point: any scored problem not dismissed. */
const trendWeightOf = (c: HotspotCanonicalData) =>
  isProblem(c) && c.state !== "dismissed" ? (c.priorityScore ?? 0) : 0;

// ── Rows ────────────────────────────────────────────────────────────

/** Ratings in the area: run-level keys take every test of the run. */
function ratingsFor(
  run: HotspotRunData,
  rowKey: string,
  by: HotspotBy,
): string[] {
  if (RUN_LEVEL.has(by)) return run.verdicts.map((v) => v.rating);
  if (by !== "human_test" || rowKey === HOTSPOT_UNLINKED_KEY) return [];
  return run.verdicts
    .filter((v) => key.humanTest(run.outlineId, v.humanTestId) === rowKey)
    .map((v) => v.rating);
}

function tally(ratings: string[]): HotspotOutcome | null {
  const o = { pass: 0, partial: 0, fail: 0 };
  for (const r of ratings) {
    if (r === "yes") o.pass++;
    else if (r === "partially") o.partial++;
    else if (r === "no") o.fail++;
  }
  return o.pass + o.partial + o.fail > 0 ? o : null;
}

const byStart = (a: HotspotRunData, b: HotspotRunData) =>
  a.startedAt.localeCompare(b.startedAt) || a.id.localeCompare(b.id);

/** Latest run per scenario — the outcome is "where it stands now". */
function latestPerScenario(runs: HotspotRunData[]): HotspotRunData[] {
  const latest = new Map<string, HotspotRunData>();
  for (const r of [...runs].sort(byStart)) latest.set(r.scenarioId, r);
  return [...latest.values()];
}

function labels(data: HotspotData): Map<string, [string, string | null]> {
  const m = new Map<string, [string, string | null]>();
  for (const c of data.entities.capabilities) {
    m.set(key.capability(c.id), [c.name, null]);
  }
  for (const s of data.entities.scenarios) {
    m.set(key.scenario(s.id), [s.label, null]);
  }
  for (const p of data.entities.personas) {
    m.set(key.persona(p.id), [p.name, null]);
  }
  for (const t of data.entities.humanTests) {
    m.set(key.humanTest(t.outlineId, t.humanTestId), [
      t.question,
      t.outlineName,
    ]);
  }
  m.set(HOTSPOT_UNLINKED_KEY, ["Not cited by any human test", null]);
  return m;
}

/** Every key the map lists even when nothing was walked there. */
function knownKeys(data: HotspotData, by: HotspotBy): string[] {
  switch (by) {
    case "capability":
      return data.entities.capabilities.map((c) => key.capability(c.id));
    case "scenario":
      return data.entities.scenarios.map((s) => key.scenario(s.id));
    case "persona":
      return data.entities.personas.map((p) => key.persona(p.id));
    case "human_test":
      return data.entities.humanTests.map((t) =>
        key.humanTest(t.outlineId, t.humanTestId),
      );
    case "route":
      return [];
  }
}

function buildRow(
  rowKey: string,
  by: HotspotBy,
  g: Groups,
  idx: Index,
  mu: number,
  k: number,
  names: Map<string, [string, string | null]>,
  alsoTagged: number | null,
  data: HotspotData,
): HotspotRow {
  const members = [...(g.canonicals.get(rowKey) ?? [])]
    .map((id) => idx.canonicals.get(id))
    .filter((c): c is HotspotCanonicalData => !!c);
  const pressuring = members.filter(addsPressure);
  const weightSum = pressuring.reduce((s, c) => s + weightOf(c), 0);
  const exposureSteps = g.exposure.get(rowKey) ?? 0;
  const runs = [...(g.runs.get(rowKey) ?? [])]
    .map((id) => idx.runs.get(id))
    .filter((r): r is HotspotRunData => !!r)
    .sort(byStart);

  const trend: HotspotTrendPoint[] = runs.map((run) => {
    const steps = runExposure(run, by, true).find(([x]) => x === rowKey)?.[1];
    const inRun = g.perRun.get(rowKey)?.get(run.id) ?? new Set<string>();
    const weight = [...inRun].reduce((s, id) => {
      const c = idx.canonicals.get(id);
      return s + (c ? trendWeightOf(c) : 0);
    }, 0);
    const o = tally(ratingsFor(run, rowKey, by));
    return {
      walkthroughId: run.id,
      startedAt: run.startedAt,
      scenarioId: run.scenarioId,
      steps: steps ?? run.steps,
      weight,
      value: round(rawPressure(weight, steps ?? run.steps), 1),
      passRate: o ? round(o.pass / (o.pass + o.partial + o.fail), 2) : null,
    };
  });

  const outcome = tally(
    latestPerScenario(runs).flatMap((r) => ratingsFor(r, rowKey, by)),
  );
  const status: HotspotStatus =
    exposureSteps <= 0
      ? "not_walked"
      : pressuring.length > 0 || (outcome != null && outcome.fail > 0)
        ? "measured"
        : "clear";
  // Clear areas read 0, not the bare prior (see the header).
  const pressure =
    status === "clear"
      ? 0
      : computePressure({ weightSum, exposureSteps, mu, k });
  const open = pressuring.filter((c) => OPEN_STATES.has(c.state));
  const [label, context] = names.get(rowKey) ?? [
    rowKey.slice(rowKey.indexOf(":") + 1),
    null,
  ];
  const runIdSet = new Set(runs.map((r) => r.id));
  const newProblems = pressuring.filter(
    (c) => c.firstSeenWalkthroughId && runIdSet.has(c.firstSeenWalkthroughId),
  ).length;
  const regressedCount = pressuring.filter(
    (c) => c.state === "regressed",
  ).length;
  const fixDidntHoldCount = pressuring.filter(
    (c) => c.stateReason === "fix_didnt_hold",
  ).length;
  const memberIdSet = new Set(members.map((m) => m.id));
  const fixedCount = new Set(
    (data.resolved ?? [])
      .filter(
        (r) =>
          runIdSet.has(r.walkthroughId) &&
          (by !== "human_test" || memberIdSet.has(r.canonicalId)),
      )
      .map((r) => r.canonicalId),
  ).size;

  const deltas: HotspotFindingDeltas = {
    new: newProblems,
    fixed: fixedCount,
    regressed: regressedCount,
    fixDidntHold: fixDidntHoldCount,
    net: newProblems + regressedCount - fixedCount,
  };

  const testRatings = new Map<
    string,
    Array<{ startedAt: string; rating: string }>
  >();
  for (const run of runs) {
    const verdicts = RUN_LEVEL.has(by)
      ? run.verdicts
      : by === "human_test" && rowKey !== HOTSPOT_UNLINKED_KEY
        ? run.verdicts.filter(
            (v) => key.humanTest(run.outlineId, v.humanTestId) === rowKey,
          )
        : [];
    for (const v of verdicts) {
      const arr = testRatings.get(v.humanTestId) ?? [];
      arr.push({ startedAt: run.startedAt, rating: v.rating });
      testRatings.set(v.humanTestId, arr);
    }
  }

  let improved = 0;
  let unchanged = 0;
  let declined = 0;
  let declinedToNo = 0;

  for (const ratings of testRatings.values()) {
    if (ratings.length < 2) {
      unchanged++;
      continue;
    }
    const sorted = [...ratings].sort((a, b) =>
      a.startedAt.localeCompare(b.startedAt),
    );
    const first = sorted[0].rating;
    const last = sorted[sorted.length - 1].rating;
    const score = (r: string) => (r === "yes" ? 2 : r === "partially" ? 1 : 0);
    const diff = score(last) - score(first);
    if (diff > 0) improved++;
    else if (diff < 0) {
      declined++;
      if (last === "no") declinedToNo++;
    } else {
      unchanged++;
    }
  }

  const verdictDeltas: HotspotVerdictDeltas = {
    improved,
    unchanged,
    declined,
    declinedToNo,
  };

  return {
    key: rowKey,
    label,
    context,
    status,
    pressure: pressure == null ? null : round(pressure),
    weightSum,
    exposureSteps,
    runs: runs.length,
    openProblems: open.length,
    awaitingRerun: pressuring.filter((c) => c.state === "fix_applied").length,
    regressed: pressuring.filter((c) => c.state === "regressed").length,
    recurring: open.filter((c) => c.observationCount > 1).length,
    strengths: members.filter(
      (c) => c.type === "helps" && c.state !== "dismissed",
    ).length,
    alsoTagged,
    personas: new Set(runs.map((r) => r.personaId)).size,
    outcome,
    trend,
    direction: trendDirection(
      trend.map((t) => t.value),
      deltas,
      verdictDeltas,
    ),
    deltas,
    verdictDeltas,
  };
}

const STATUS_RANK: Record<HotspotStatus, number> = {
  measured: 0,
  clear: 1,
  not_walked: 2,
};
const passRate = (o: HotspotOutcome | null) =>
  o ? o.pass / (o.pass + o.partial + o.fail) : 2;

/**
 * Default order: areas with failing human tests or active regressions at the top,
 * then measured pressure, then lower pass rate, then clear areas, then not walked.
 */
export function compareHotspotRows(a: HotspotRow, b: HotspotRow): number {
  if (STATUS_RANK[a.status] !== STATUS_RANK[b.status]) {
    return STATUS_RANK[a.status] - STATUS_RANK[b.status];
  }
  if (a.status === "measured") {
    const aFail = a.outcome?.fail ?? 0;
    const bFail = b.outcome?.fail ?? 0;
    if (bFail !== aFail) return bFail - aFail;

    if (b.regressed !== a.regressed) return b.regressed - a.regressed;

    return (
      (b.pressure ?? -1) - (a.pressure ?? -1) ||
      passRate(a.outcome) - passRate(b.outcome) ||
      a.label.localeCompare(b.label)
    );
  }
  if (a.status === "clear") {
    return (
      passRate(a.outcome) - passRate(b.outcome) ||
      b.exposureSteps - a.exposureSteps ||
      a.label.localeCompare(b.label)
    );
  }
  return a.label.localeCompare(b.label);
}

function coverage(data: HotspotData, idx: Index): HotspotList["coverage"] {
  const steps = data.runs.reduce((s, r) => s + r.steps, 0);
  const capSteps = data.runs
    .filter((r) => r.primaryCapabilityId)
    .reduce((s, r) => s + r.steps, 0);
  const routeSteps = data.runs.reduce(
    (s, r) => s + Object.values(r.routeSteps).reduce((a, b) => a + b, 0),
    0,
  );
  const obs = data.observations.filter((o) =>
    idx.canonicals.has(o.canonicalId),
  );
  const citedObs = obs.filter((o) =>
    idx.cited.has(`${o.walkthroughId}|${o.findingId}`),
  ).length;
  const all = { keyed: steps, total: steps, unit: "steps" as const };
  return {
    capability: { keyed: capSteps, total: steps, unit: "steps" },
    scenario: all,
    persona: all,
    human_test: { keyed: citedObs, total: obs.length, unit: "findings" },
    route: { keyed: routeSteps, total: steps, unit: "steps" },
  };
}

function priorOf(data: HotspotData, idx: Index, k: number): HotspotPrior {
  const seen = new Set(
    data.observations
      .filter((o) => idx.runs.has(o.walkthroughId))
      .map((o) => o.canonicalId),
  );
  const weight = [...seen].reduce((s, id) => {
    const c = idx.canonicals.get(id);
    return s + (c ? weightOf(c) : 0);
  }, 0);
  const steps = data.runs.reduce((s, r) => s + r.steps, 0);
  return { k, mu: round(pressurePrior(weight, steps), 4) };
}

/** Secondary-tag counts per capability key (the "also tagged" column). */
function alsoTaggedCounts(data: HotspotData, idx: Index): Map<string, number> {
  const m = new Map<string, Set<string>>();
  for (const obs of data.observations) {
    const run = idx.runs.get(obs.walkthroughId);
    if (!run || !idx.canonicals.has(obs.canonicalId)) continue;
    for (const id of run.secondaryCapabilityIds) {
      if (id !== run.primaryCapabilityId) {
        addTo(m, key.capability(id), obs.canonicalId);
      }
    }
  }
  return new Map([...m].map(([k, s]) => [k, s.size]));
}

/** The ranked hotspot list for one grouping key. */
export function buildHotspotList(
  data: HotspotData,
  by: HotspotBy,
  opts: HotspotOptions = {},
): HotspotList {
  const k = opts.k ?? HOTSPOT_PRIOR_K;
  const includeSecondary = opts.includeSecondary ?? false;
  const idx = buildIndex(data);
  const prior =
    opts.mu === undefined ? priorOf(data, idx, k) : { k, mu: opts.mu };
  const g = groupData(data, idx, by, includeSecondary);
  const names = labels(data);
  const also = by === "capability" ? alsoTaggedCounts(data, idx) : null;
  const keys = new Set([
    ...knownKeys(data, by),
    ...g.exposure.keys(),
    ...g.canonicals.keys(),
  ]);
  const rows = [...keys]
    .map((rk) =>
      buildRow(
        rk,
        by,
        g,
        idx,
        prior.mu,
        k,
        names,
        also ? (also.get(rk) ?? 0) : null,
        data,
      ),
    )
    .sort(compareHotspotRows);

  const problems = new Set(
    data.observations
      .map((o) => idx.canonicals.get(o.canonicalId))
      .filter((c): c is HotspotCanonicalData => !!c && addsPressure(c))
      .map((c) => c.id),
  );
  return {
    by,
    rows,
    coverage: coverage(data, idx),
    prior,
    totals: {
      runs: data.runs.length,
      steps: data.runs.reduce((s, r) => s + r.steps, 0),
      problems: problems.size,
      weightSum: [...problems].reduce(
        (s, id) => s + weightOf(idx.canonicals.get(id) as HotspotCanonicalData),
        0,
      ),
    },
  };
}

const member = (c: HotspotCanonicalData): HotspotMember => ({ ...c });
const byScore = (a: HotspotCanonicalData, b: HotspotCanonicalData) =>
  (b.priorityScore ?? -1) - (a.priorityScore ?? -1) ||
  (a.title ?? "").localeCompare(b.title ?? "");

/** One hotspot with its failing tests, members and distribution. */
export function buildHotspotDetail(
  data: HotspotData,
  by: HotspotBy,
  rowKey: string,
  opts: HotspotOptions = {},
): HotspotDetail | null {
  const list = buildHotspotList(data, by, opts);
  const row = list.rows.find((r) => r.key === rowKey);
  if (!row) return null;
  const idx = buildIndex(data);
  const includeSecondary = opts.includeSecondary ?? false;
  const g = groupData(data, idx, by, includeSecondary);
  const members = [...(g.canonicals.get(rowKey) ?? [])]
    .map((id) => idx.canonicals.get(id))
    .filter((c): c is HotspotCanonicalData => !!c)
    .sort(byScore);
  const memberIds = new Set(members.map((c) => c.id));

  const distribution: HotspotDetail["distribution"] = {};
  const names = labels(data);
  const problemIds = new Set(members.filter(isProblem).map((c) => c.id));
  for (const other of [
    "capability",
    "persona",
    "scenario",
    "human_test",
    "route",
  ] as const) {
    if (other === by) continue;
    const og = groupData(data, idx, other, includeSecondary);
    const counts = [...og.canonicals]
      .map(([k, ids]) => ({
        key: k,
        label: names.get(k)?.[0] ?? k.slice(k.indexOf(":") + 1),
        count: [...ids].filter((id) => problemIds.has(id)).length,
      }))
      .filter((d) => d.count > 0 && d.key !== HOTSPOT_UNLINKED_KEY)
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    if (counts.length) distribution[other] = counts;
  }

  return {
    row,
    prior: list.prior,
    failingTests: failingTests(data, idx, g, by, rowKey, memberIds),
    members: {
      pressure: members.filter(addsPressure).map(member),
      other: members
        .filter((c) => isProblem(c) && !addsPressure(c))
        .map(member),
    },
    strengths: members.filter((c) => c.type === "helps").map(member),
    distribution,
  };
}

/** Human tests in the area whose latest rating is "no" or "partially". */
function failingTests(
  data: HotspotData,
  idx: Index,
  g: Groups,
  by: HotspotBy,
  rowKey: string,
  memberIds: Set<string>,
): HotspotFailingTest[] {
  // Runs to read ratings from: the area's runs, or (page / unlinked) the
  // runs whose judge cited one of the area's findings.
  const findingCanonical = new Map(
    data.observations.map((o) => [`${o.walkthroughId}|${o.findingId}`, o]),
  );
  const tests = new Map<string, { outlineId: string; humanTestId: string }>();
  const areaRuns = [...(g.runs.get(rowKey) ?? [])];
  if (
    RUN_LEVEL.has(by) ||
    (by === "human_test" && rowKey !== HOTSPOT_UNLINKED_KEY)
  ) {
    for (const id of areaRuns) {
      const run = idx.runs.get(id);
      for (const v of run?.verdicts ?? []) {
        const tk = key.humanTest(run!.outlineId, v.humanTestId);
        if (by === "human_test" && tk !== rowKey) continue;
        tests.set(tk, {
          outlineId: run!.outlineId,
          humanTestId: v.humanTestId,
        });
      }
    }
  } else {
    for (const c of data.citations) {
      const o = findingCanonical.get(`${c.walkthroughId}|${c.findingId}`);
      const run = idx.runs.get(c.walkthroughId);
      if (!o || !run || !memberIds.has(o.canonicalId)) continue;
      tests.set(key.humanTest(run.outlineId, c.humanTestId), {
        outlineId: run.outlineId,
        humanTestId: c.humanTestId,
      });
    }
  }

  const info = new Map(
    data.entities.humanTests.map((t) => [
      key.humanTest(t.outlineId, t.humanTestId),
      t,
    ]),
  );
  const out: HotspotFailingTest[] = [];
  for (const [tk, t] of tests) {
    const runs = data.runs
      .filter((r) => r.outlineId === t.outlineId)
      .filter((r) => (RUN_LEVEL.has(by) ? areaRuns.includes(r.id) : true))
      .sort(byStart);
    const ratings = runs.flatMap((r) =>
      r.verdicts
        .filter((v) => v.humanTestId === t.humanTestId)
        .map((v) => ({
          walkthroughId: r.id,
          startedAt: r.startedAt,
          rating: v.rating,
        })),
    );
    const latest = ratings[ratings.length - 1]?.rating;
    if (latest !== "no" && latest !== "partially") continue;
    const runIds = new Set(ratings.map((r) => r.walkthroughId));
    const cited = new Set<string>();
    for (const c of data.citations) {
      if (c.humanTestId !== t.humanTestId || !runIds.has(c.walkthroughId)) {
        continue;
      }
      const o = findingCanonical.get(`${c.walkthroughId}|${c.findingId}`);
      if (o) cited.add(o.canonicalId);
    }
    const citedCanonicals = [...cited]
      .map((id) => idx.canonicals.get(id))
      .filter((c): c is HotspotCanonicalData => !!c)
      .sort(byScore);
    out.push({
      outlineId: t.outlineId,
      humanTestId: t.humanTestId,
      question: info.get(tk)?.question ?? t.humanTestId,
      scenarioName: info.get(tk)?.outlineName ?? "",
      ratings,
      citedCanonicalIds: citedCanonicals.map((c) => c.id),
    });
  }
  const worst = (t: HotspotFailingTest) =>
    t.ratings[t.ratings.length - 1]?.rating === "no" ? 0 : 1;
  return out.sort(
    (a, b) => worst(a) - worst(b) || a.question.localeCompare(b.question),
  );
}

// ── Matrix: capability × persona ────────────────────────────────────

export interface HotspotMatrixCell {
  pressure: number | null;
  status: HotspotStatus;
  weightSum: number;
  exposureSteps: number;
}

export interface HotspotMatrix {
  rows: Array<{ key: string; label: string }>;
  cols: Array<{ key: string; label: string }>;
  /** cells[rowKey][colKey] */
  cells: Record<string, Record<string, HotspotMatrixCell>>;
  prior: HotspotPrior;
}

/**
 * "Is this weakness universal or one persona's?" — capability pressure
 * per persona, each cell smoothed toward the same project prior. Rows
 * in the capability ranking's order.
 */
export function buildHotspotMatrix(
  data: HotspotData,
  opts: HotspotOptions = {},
): HotspotMatrix {
  const overall = buildHotspotList(data, "capability", opts);
  const cells: HotspotMatrix["cells"] = {};
  const cols = data.entities.personas.map((p) => ({
    key: key.persona(p.id),
    label: p.name,
    id: p.id,
  }));
  for (const col of cols) {
    const runs = data.runs.filter((r) => r.personaId === col.id);
    const ids = new Set(runs.map((r) => r.id));
    const list = buildHotspotList(
      {
        ...data,
        runs,
        observations: data.observations.filter((o) => ids.has(o.walkthroughId)),
      },
      "capability",
      { ...opts, mu: overall.prior.mu },
    );
    for (const row of list.rows) {
      cells[row.key] ??= {};
      cells[row.key][col.key] = {
        pressure: row.pressure,
        status: row.status,
        weightSum: row.weightSum,
        exposureSteps: row.exposureSteps,
      };
    }
  }
  return {
    rows: overall.rows.map((r) => ({ key: r.key, label: r.label })),
    cols: cols.map(({ key: k, label }) => ({ key: k, label })),
    cells,
    prior: overall.prior,
  };
}
